import { defineStore } from "pinia"
import { useAuthStore } from "./auth"

export const useCarrierStore = defineStore("carrier", {
  state: () => ({
    carrier: null,
    documents: [],
    ratings: [],
    metrics: null,
    incidents: [],
    loading: false,
    drivers: [],
    vehicles: [],
    shipments: [],
    cargoRequests: [],
    forwarderAssignments: [],
    assignmentStats: { pending: 0, accepted: 0, declined: 0, expired: 0 },
    error: null,
  }),

  getters: {
    isActive: (state) => {
      const authStore = useAuthStore()
      return authStore.user?.status === "active"
    },
    isSuspended: (state) => {
      const authStore = useAuthStore()
      return authStore.user?.status === "suspended"
    },
    isBanned: (state) => {
      const authStore = useAuthStore()
      return authStore.user?.status === "banned"
    },
    isInReview: (state) => {
      const authStore = useAuthStore()
      return authStore.user?.status === "inreview"
    },
    isPending: (state) => {
      const authStore = useAuthStore()
      return authStore.user?.status === "pending"
    },
    isUnderReview: (state) => {
      const authStore = useAuthStore()
      const userStatus = authStore.user?.status
      return userStatus === "inreview" || userStatus === "pending"
    },
    canManage: (state) => {
      const authStore = useAuthStore()
      const userStatus = authStore.user?.status
      return userStatus === "active" && !["inreview", "pending", "suspended", "banned"].includes(userStatus)
    },
    hasDocuments: (state) => state.documents.length > 0,
    hasRatings: (state) => state.ratings.length > 0,
    averageRating: (state) => {
      if (!state.ratings.length) return 0
      const sum = state.ratings.reduce((acc, rating) => acc + rating.rating, 0)
      return sum / state.ratings.length
    },
    getDrivers: (state) => state.drivers,
    getVehicles: (state) => state.vehicles,
    getShipments: (state) => state.shipments,
    getCargoRequests: (state) => state.cargoRequests,
    getForwarderAssignments: (state) => state.forwarderAssignments,
    getAssignmentStats: (state) => state.assignmentStats,
    getPendingAssignments: (state) => state.forwarderAssignments.filter((a) => a.status === "pending"),
    getAcceptedAssignments: (state) => state.forwarderAssignments.filter((a) => a.status === "accepted"),
    getDeclinedAssignments: (state) => state.forwarderAssignments.filter((a) => a.status === "declined"),
    getExpiredAssignments: (state) => state.forwarderAssignments.filter((a) => a.status === "expired"),
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
  },

  actions: {
    // Helper function to check if management actions are allowed
    checkManagementPermission(actionName = "this action") {
      if (!this.canManage) {
        const { $toast } = useNuxtApp()
        const authStore = useAuthStore()
        const userStatus = authStore.user?.status

        let message = "Your account is under review. Management actions are not allowed until your account is verified."

        if (userStatus === "inreview") {
          message = "Your account is currently under review. Management actions are not allowed until verification is complete."
        } else if (userStatus === "pending") {
          message = "Your account is pending approval. Management actions are not allowed until your account is verified."
        } else if (userStatus === "suspended") {
          message = "Your account is suspended. Management actions are not allowed."
        } else if (userStatus === "banned") {
          message = "Your account is banned. Management actions are not allowed."
        }

        $toast.error(message)
        return false
      }
      return true
    },

    // Registration Document Upload
    async uploadRegistrationDocument(file) {
      try {
        this.loading = true
        const formData = new FormData()
        formData.append("file", file)
        formData.append("type", "tin")

        const response = await this.$axios.post("/api/v1/documents/upload/carrier/tin", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

        return { success: true, data: response.data.data, file_path: response.data.data.file_path }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to upload TIN document"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchCarrierDetails() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/fleet/carrier")
        this.carrier = response.data
        // this.drivers = response.data.data.drivers
        return { success: true, data: response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch carrier details"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async updateCarrierProfile(formData) {
      if (!this.checkManagementPermission("updating carrier profile")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/fleet/carrier/${this.carrier.id}`, formData)
        this.carrier = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update carrier profile"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async uploadCarrierLogo(formData) {
      if (!this.checkManagementPermission("uploading carrier logo")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/fleet/carrier/${this.carrier.id}/logo`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        // Update user data in store
        if (response.data.logo_url) {
          this.carrier = {
            ...this.user,
            logo: response.data.logo_url,
          }
        }
        return { success: true, data: response.data.data, logo_url: response.data.logo_url }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to upload carrier logo"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async removeCarrierLogo() {
      if (!this.checkManagementPermission("removing carrier logo")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.delete(`/api/v1/fleet/carrier/${this.carrier.id}/logo`)
        this.carrier.logo = null
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to remove carrier logo"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchDrivers() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/drivers`)
        this.drivers = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        console.log(error)
        this.error = error.response?.data?.message || "Failed to fetch drivers"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async addDriver(driverData) {
      if (!this.checkManagementPermission("adding drivers")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/fleet/drivers", driverData)
        this.fetchDrivers()
        return {
          success: true,
          data: response.data.data,
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || "Failed to create driver",
          errors: error.response?.data?.errors || null,
          driver_id: error.response?.data?.driver_id || null,
        }
      } finally {
        this.loading = false
      }
    },
    async updateDriver(driverId, driverData) {
      if (!this.checkManagementPermission("updating drivers")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/fleet/drivers/${driverId}`, driverData)
        this.fetchDrivers()
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update driver"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },
    async fetchVehicles() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/vehicles`)
        this.vehicles = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch vehicles"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },
    async addVehicle(vehicleData) {
      if (!this.checkManagementPermission("adding vehicles")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/fleet/vehicles", vehicleData)
        this.fetchVehicles()
        return { success: true, data: response.data.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.message || "Failed to create vehicle" }
      } finally {
        this.loading = false
      }
    },
    async updateVehicle(vehicleId, vehicleData) {
      if (!this.checkManagementPermission("updating vehicles")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/fleet/vehicles/${vehicleId}`, vehicleData)
        this.fetchVehicles()
        return { success: true, data: response.data.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.message || "Failed to update vehicle" }
      } finally {
        this.loading = false
      }
    },

    async assignDriver(vehicleId, formData) {
      if (!this.checkManagementPermission("assigning drivers to vehicles")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/fleet/vehicles/${vehicleId}/assign`, formData)
        this.fetchVehicles()
        return { success: true, data: response.data.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.message, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },
    async fetchDocuments() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/${this.carrier.id}/documents`)
        this.documents = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch documents"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async uploadDocument(formData) {
      if (!this.checkManagementPermission("uploading documents")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/fleet/${this.carrier.id}/documents`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        this.documents.push(response.data.data)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to upload document"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async deleteDocument(documentId) {
      if (!this.checkManagementPermission("deleting documents")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.delete(`/api/v1/fleet/${this.carrier.id}/documents/${documentId}`)
        // Handle both 200 OK and 204 No Content as success
        if (response.status === 200 || response.status === 204) {
          this.documents = this.documents.filter((doc) => doc.id !== documentId)
          return { success: true }
        }
        return { success: false, error: "Unexpected response status" }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || "Failed to delete document"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchRatings() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/carriers/${this.carrier.id}/ratings`)
        this.ratings = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch ratings"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchMetrics() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/carriers/${this.carrier.id}/metrics`)
        this.metrics = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch metrics"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async fetchIncidents() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/carriers/${this.carrier.id}/incidents`)
        this.incidents = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch incidents"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async reportIncident(formData) {
      if (!this.checkManagementPermission("reporting incidents")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/fleet/carriers/${this.carrier.id}/incidents`, formData)
        this.incidents.push(response.data.data)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to report incident"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async updateIncidentStatus(incidentId, status) {
      if (!this.checkManagementPermission("updating incident status")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/fleet/carriers/${this.carrier.id}/incidents/${incidentId}`, { status })
        const index = this.incidents.findIndex((inc) => inc.id === incidentId)
        if (index !== -1) {
          this.incidents[index] = response.data.data
        }
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update incident status"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    // Shipment Management
    async fetchShipments() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/shipment/shipments")
        this.shipments = response.data.data || []
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch shipments"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async createShipment(shipmentData) {
      if (!this.checkManagementPermission("creating shipments")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/shipment/shipments", shipmentData)
        this.shipments.push(response.data.data)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create shipment"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    async updateShipment(shipmentId, shipmentData) {
      if (!this.checkManagementPermission("updating shipments")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipment/shipments/${shipmentId}`, shipmentData)
        const index = this.shipments.findIndex((s) => s.id === shipmentId)
        if (index !== -1) {
          this.shipments[index] = response.data.data
        }
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update shipment"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    // Cargo Request Management
    async fetchCargoRequests() {
      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/shipment/cargo-requests")
        this.cargoRequests = response.data.data || []
        return { success: true, data: response.data.data }
      } catch (error) {
        // If cargo requests are not available for carriers (backend limitation),
        // return empty array instead of error
        if (error.response?.status === 403 || error.response?.status === 404) {
          this.cargoRequests = []
          return { success: true, data: [] }
        }
        this.error = error.response?.data?.message || "Failed to fetch cargo requests"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async acceptCargoRequest(requestId, requestData = {}) {
      if (!this.checkManagementPermission("accepting cargo requests")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/cargo-requests/${requestId}/accept`, {
          ...requestData,
          status: "accepted",
          carrier_id: this.carrier.id,
        })

        // Update the request in the local state
        const index = this.cargoRequests.findIndex((r) => r.id === requestId)
        if (index !== -1) {
          this.cargoRequests[index] = response.data.data.cargo_request
        }

        return { success: true, data: response.data.data.cargo_request }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to accept cargo request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    // async createShipmentFromRequest(requestId, shipmentData) {
    //   if (!this.checkManagementPermission("creating shipments")) {
    //     return { success: false, error: "Management action not allowed" }
    //   }

    //   try {
    //     this.loading = true
    //     // Using this.$axios injected by pinia plugin
    //     const response = await this.$axios.post(`/api/v1/shipment/cargo-requests/${requestId}/create-shipment`, shipmentData)

    //     // Add the new shipment to the local state
    //     this.shipments.push(response.data.data)

    //     return { success: true, data: response.data.data }
    //   } catch (error) {
    //     this.error = error.response?.data?.message || "Failed to create shipment"
    //     return { success: false, error: this.error, errors: error.response?.data?.errors || null }
    //   } finally {
    //     this.loading = false
    //   }
    // },

    async assignDriverToShipment(shipmentId, driverId) {
      if (!this.checkManagementPermission("assigning drivers")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/shipments/${shipmentId}/assign-driver`, {
          driver_id: driverId,
        })

        // Update the shipment in the local state
        const index = this.shipments.findIndex((s) => s.id === shipmentId)
        if (index !== -1) {
          this.shipments[index] = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to assign driver"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    async rejectCargoRequest(requestId, reason = null) {
      if (!this.checkManagementPermission("rejecting cargo requests")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/cargo-requests/${requestId}/reject`, {
          status: "rejected",
          rejection_reason: reason,
          carrier_id: this.carrier.id,
        })

        // Update the request in the local state
        const index = this.cargoRequests.findIndex((r) => r.id === requestId)
        if (index !== -1) {
          this.cargoRequests[index] = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to reject cargo request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    // Forwarder Assignment Actions
    async fetchForwarderAssignments(filters = {}) {
      if (!this.checkManagementPermission("fetching forwarder assignments")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/carrier-assignments", { params: filters })
        this.forwarderAssignments = response.data.data || []
        this.assignmentStats = response.data.stats || { pending: 0, accepted: 0, declined: 0, expired: 0 }
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch forwarder assignments"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async getAssignmentDetails(assignmentId) {
      if (!this.checkManagementPermission("fetching assignment details")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/carrier-assignments/${assignmentId}`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch assignment details"
        return { success: false, error: this.error }
      } finally {
        this.loading = false
      }
    },

    async acceptAssignment(assignmentId, acceptanceData) {
      if (!this.checkManagementPermission("accepting assignments")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/carrier-assignments/${assignmentId}/accept`, acceptanceData)

        // Update local state
        const index = this.forwarderAssignments.findIndex((a) => a.id === assignmentId)
        if (index !== -1) {
          this.forwarderAssignments[index] = response.data.data
        }

        // Refresh assignments to get updated stats
        await this.fetchForwarderAssignments()

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to accept assignment"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    async declineAssignment(assignmentId, declineData) {
      if (!this.checkManagementPermission("declining assignments")) {
        return { success: false, error: "Management action not allowed" }
      }

      try {
        this.loading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/carrier-assignments/${assignmentId}/decline`, declineData)

        // Update local state
        const index = this.forwarderAssignments.findIndex((a) => a.id === assignmentId)
        if (index !== -1) {
          this.forwarderAssignments[index] = response.data.data
        }

        // Refresh assignments to get updated stats
        await this.fetchForwarderAssignments()

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to decline assignment"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.loading = false
      }
    },

    clearError() {
      this.error = null
    },

    reset() {
      this.carrier = null
      this.documents = []
      this.ratings = []
      this.metrics = null
      this.incidents = []
      this.shipments = []
      this.cargoRequests = []
      this.forwarderAssignments = []
      this.assignmentStats = { pending: 0, accepted: 0, declined: 0, expired: 0 }
      this.loading = false
      this.error = null
    },
  },
})
